import Parse from 'parse';
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Alert
} from '@mui/material';

export const EditProfile = ({ open, onClose, onSuccess }) => {
  const currentUser = Parse.User.current();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Allow empty phone or exactly 10 digits
    if (!phone) return true;
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validateUsername = (username) => {
    // Username must contain at least one letter (cannot be only numbers)
    const hasLetter = /[a-zA-Z]/.test(username);
    return hasLetter;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const username = formData.get('username')?.trim();
    const email = formData.get('email')?.trim();
    const phone = formData.get('phone')?.trim();

    try {
      // Validation
      if (!username) {
        setError('Username is required');
        setLoading(false);
        return;
      }

      if (!validateUsername(username)) {
        setError('Username cannot contain only numbers. Please include at least one letter.');
        setLoading(false);
        return;
      }

      if (!email) {
        setError('Email is required');
        setLoading(false);
        return;
      }

      if (!validateEmail(email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      if (phone && !validatePhone(phone)) {
        setError('Please enter a valid phone number (exactly 10 digits)');
        setLoading(false);
        return;
      }

      // Check if phone number is already in use by another user
      if (phone && phone !== currentUser?.get('phone')) {
        const phoneQuery = new Parse.Query(Parse.User);
        phoneQuery.equalTo('phone', phone);
        phoneQuery.notEqualTo('objectId', currentUser?.id);
        const existingUser = await phoneQuery.first({ useMasterKey: true });

        if (existingUser) {
          setError('This phone number is already in use');
          setLoading(false);
          return;
        }
      }

      if (currentUser) {
        // Update user fields
        currentUser.set('username', username);
        currentUser.set('email', email);
        if (phone) {
          currentUser.set('phone', phone);
        }

        await currentUser.save();

        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#242424',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Edit User Profile
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="adminId"
                label="Admin ID"
                type="text"
                fullWidth
                defaultValue={currentUser?.id || ''}
                disabled
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.5)'
                  },
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="username"
                label="Username"
                type="text"
                fullWidth
                defaultValue={currentUser?.get('username') || ''}
                required
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFC107'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email"
                type="email"
                fullWidth
                defaultValue={currentUser?.get('email') || ''}
                required
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFC107'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phone"
                label="Phone"
                type="tel"
                fullWidth
                defaultValue={currentUser?.get('phone') || ''}
                placeholder="1234567890"
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]*',
                  inputMode: 'numeric'
                }}
                onInput={(e) => {
                  // Only allow numeric input
                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFC107'
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: '#fff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#FFC107',
              color: '#000',
              '&:hover': {
                backgroundColor: '#FFD54F'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 193, 7, 0.5)',
                color: 'rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
